import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { packingSlipService } from '../../services/api';
import { toast } from 'react-toastify';
import { Combobox } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';

const PackingSlipsList = () => {
    const navigate = useNavigate();
    const [packingSlips, setPackingSlips] = useState([]);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        total: 0
    });
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState({
        status: '',
        search: '',
        page: 1
    });
    const [batchesWithoutSlips, setBatchesWithoutSlips] = useState([]);
    const [selectedBatch, setSelectedBatch] = useState(null);
    const [batchQuery, setBatchQuery] = useState('');
    const [creatingSlip, setCreatingSlip] = useState(false);
    const [editingSlipId, setEditingSlipId] = useState(null);
    const [editingStatus, setEditingStatus] = useState('');
    const [selectedSlipIds, setSelectedSlipIds] = useState([]);

    const loadPackingSlips = async () => {
        try {
            setLoading(true);
            const response = await packingSlipService.getPackingSlips(filter);
            setPackingSlips(response.data.data);
            setPagination({
                currentPage: response.data.current_page,
                totalPages: response.data.last_page,
                total: response.data.total
            });
        } catch (error) {
            console.error('Error fetching packing slips:', error);
            toast.error('Failed to load packing slips');
        } finally {
            setLoading(false);
        }
    };

    const loadBatchesWithoutSlips = async () => {
        try {
            const response = await packingSlipService.getBatchesWithoutSlips();
            setBatchesWithoutSlips(response.data);
        } catch (error) {
            console.error('Error fetching batches without slips:', error);
            toast.error('Failed to load batches without packing slips');
        }
    };

    const handleCreatePackingSlip = async () => {
        if (!selectedBatch) {
            toast.error('Please select a batch');
            return;
        }

        try {
            setCreatingSlip(true);
            await packingSlipService.createPackingSlip({
                production_batch_id: selectedBatch.id
            });
            toast.success('Packing slip created successfully');
            setSelectedBatch(null);
            setBatchQuery('');
            loadPackingSlips();
            loadBatchesWithoutSlips();
        } catch (error) {
            console.error('Error creating packing slip:', error);
            toast.error(error.response?.data?.message || 'Failed to create packing slip');
        } finally {
            setCreatingSlip(false);
        }
    };

    const handleEditStatus = (slip) => {
        setEditingSlipId(slip.id);
        setEditingStatus(slip.status);
    };

    const handleSaveStatus = async (slipId) => {
        try {
            await packingSlipService.updatePackingSlip(slipId, { status: editingStatus });
            toast.success('Status updated successfully');
            setEditingSlipId(null);
            loadPackingSlips();
        } catch (error) {
            console.error('Error updating status:', error);
            toast.error('Failed to update status');
        }
    };

    const handleCancelEdit = () => {
        setEditingSlipId(null);
        setEditingStatus('');
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedSlipIds(packingSlips.map(slip => slip.id));
        } else {
            setSelectedSlipIds([]);
        }
    };

    const handleSelectSlip = (slipId) => {
        setSelectedSlipIds(prev => {
            if (prev.includes(slipId)) {
                return prev.filter(id => id !== slipId);
            } else {
                return [...prev, slipId];
            }
        });
    };

    const filteredBatches = batchQuery === ''
        ? batchesWithoutSlips
        : batchesWithoutSlips.filter((batch) => {
            const searchStr = batchQuery.toLowerCase();
            return (
                batch.batch_number.toLowerCase().includes(searchStr) ||
                format(new Date(batch.actual_completion_date), 'MM/dd/yyyy').includes(searchStr)
            );
        });

    useEffect(() => {
        loadPackingSlips();
        loadBatchesWithoutSlips();
    }, [filter]);

    const handleFilterChange = (key, value) => {
        setFilter(prev => ({ ...prev, [key]: value, page: 1 }));
    };

    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= pagination.totalPages) {
            setFilter(prev => ({ ...prev, page: newPage }));
        }
    };

    const renderStatusBadge = (status) => {
        const statusMap = {
            'pending': 'PENDING',
            'packed': 'PACKED',
            'shipped': 'SHIPPED',
            'received': 'RECEIVED'
        };

        const classes = {
            'PENDING': 'bg-gray-100 text-gray-800',
            'PACKED': 'bg-blue-100 text-blue-800',
            'SHIPPED': 'bg-yellow-100 text-yellow-800',
            'RECEIVED': 'bg-green-100 text-green-800'
        };

        const displayStatus = statusMap[status] || status.toUpperCase();

        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${classes[displayStatus] || 'bg-gray-100 text-gray-800'}`}>
                {displayStatus}
            </span>
        );
    };

    const handlePrintSelected = () => {
        if (selectedSlipIds.length === 0) {
            toast.error('Please select at least one packing slip to print');
            return;
        }
        // Navigate to bulk print page with selected IDs
        navigate(`/packing-slips/print-bulk?ids=${selectedSlipIds.join(',')}`);
    };

    return (
        <div className="container mx-auto px-4 py-6">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Packing Slips</h1>
                {selectedSlipIds.length > 0 && (
                    <button
                        onClick={handlePrintSelected}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center"
                    >
                        <svg className="h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a2 2 0 002 2h6a2 2 0 002-2v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a2 2 0 00-2-2H7a2 2 0 00-2 2zm8 0H7v3h6V4zm0 8H7v4h6v-4z" clipRule="evenodd" />
                        </svg>
                        Print Selected ({selectedSlipIds.length})
                    </button>
                )}
            </div>

            {/* Create Packing Slip for Old Batches */}
            {batchesWithoutSlips.length > 0 && (
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
                    <div className="flex items-center justify-between">
                        <div className="flex-1">
                            <h3 className="text-sm font-medium text-blue-800 mb-2">
                                Create Packing Slips for Completed Batches
                            </h3>
                            <p className="text-sm text-blue-700 mb-3">
                                {batchesWithoutSlips.length} completed {batchesWithoutSlips.length === 1 ? 'batch' : 'batches'} without packing slips
                            </p>
                            <div className="flex items-center gap-3">
                                <Combobox value={selectedBatch} onChange={setSelectedBatch}>
                                    <div className="relative w-96">
                                        <div className="relative">
                                            <Combobox.Input
                                                className="w-full rounded-md border border-blue-300 bg-white py-2 pl-3 pr-10 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                                displayValue={(batch) => batch ? `${batch.batch_number} - Completed ${format(new Date(batch.actual_completion_date), 'MM/dd/yyyy')} (${batch.items?.length || 0} items)` : ''}
                                                onChange={(event) => setBatchQuery(event.target.value)}
                                                placeholder="Search batches..."
                                            />
                                            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                            </Combobox.Button>
                                        </div>
                                        <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                            {filteredBatches.length === 0 && batchQuery !== '' ? (
                                                <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                                    No batches found.
                                                </div>
                                            ) : (
                                                filteredBatches.map((batch) => (
                                                    <Combobox.Option
                                                        key={batch.id}
                                                        value={batch}
                                                        className={({ active }) =>
                                                            `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                                                active ? 'bg-blue-600 text-white' : 'text-gray-900'
                                                            }`
                                                        }
                                                    >
                                                        {({ selected, active }) => (
                                                            <>
                                                                <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                                                    {batch.batch_number} - Completed {format(new Date(batch.actual_completion_date), 'MM/dd/yyyy')} ({batch.items?.length || 0} items)
                                                                </span>
                                                                {selected ? (
                                                                    <span
                                                                        className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                                                            active ? 'text-white' : 'text-blue-600'
                                                                        }`}
                                                                    >
                                                                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                                                    </span>
                                                                ) : null}
                                                            </>
                                                        )}
                                                    </Combobox.Option>
                                                ))
                                            )}
                                        </Combobox.Options>
                                    </div>
                                </Combobox>
                                <button
                                    onClick={handleCreatePackingSlip}
                                    disabled={!selectedBatch || creatingSlip}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {creatingSlip ? 'Creating...' : 'Create Packing Slip'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-800">All Packing Slips</h2>
                </div>

                <div className="p-6">
                    <div className="flex flex-wrap gap-4 mb-6">
                        <div className="w-64">
                            <input
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Search by packing slip or batch number..."
                                value={filter.search}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                            />
                        </div>
                        <div className="w-48">
                            <select
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={filter.status}
                                onChange={(e) => handleFilterChange('status', e.target.value)}
                            >
                                <option value="">All Status</option>
                                <option value="pending">Pending</option>
                                <option value="packed">Packed</option>
                                <option value="shipped">Shipped</option>
                                <option value="received">Received</option>
                            </select>
                        </div>
                        <button
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            onClick={() => setFilter({ status: '', search: '', page: 1 })}
                        >
                            Reset Filters
                        </button>
                    </div>

                    {loading ? (
                        <div className="flex justify-center items-center h-64">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                        </div>
                    ) : packingSlips.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            No packing slips found
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left">
                                            <input
                                                type="checkbox"
                                                checked={selectedSlipIds.length === packingSlips.length && packingSlips.length > 0}
                                                onChange={handleSelectAll}
                                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                            />
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Packing Slip #
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Batch #
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Date Created
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Date Packed
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Date Shipped
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Date Received
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Items
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {packingSlips.map((slip) => (
                                        <tr key={slip.id} className="hover:bg-gray-50" onClick={(e) => {
                                            // Only navigate if not clicking on buttons, select, or checkbox
                                            if (!e.target.closest('button') && !e.target.closest('select') && !e.target.closest('input[type="checkbox"]')) {
                                                navigate(`/packing-slips/${slip.id}`);
                                            }
                                        }}>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedSlipIds.includes(slip.id)}
                                                    onChange={() => handleSelectSlip(slip.id)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                />
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {slip.packing_slip_number}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {slip.production_batch?.batch_number || 'N/A'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {format(new Date(slip.created_at), 'MM/dd/yyyy')}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {slip.date_packed ? format(new Date(slip.date_packed), 'MM/dd/yyyy') : '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {slip.date_shipped ? format(new Date(slip.date_shipped), 'MM/dd/yyyy') : '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {slip.date_received ? format(new Date(slip.date_received), 'MM/dd/yyyy') : '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {editingSlipId === slip.id ? (
                                                    <select
                                                        value={editingStatus}
                                                        onChange={(e) => setEditingStatus(e.target.value)}
                                                        className="rounded-md border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <option value="pending">Pending</option>
                                                        <option value="packed">Packed</option>
                                                        <option value="shipped">Shipped</option>
                                                        <option value="received">Received</option>
                                                    </select>
                                                ) : (
                                                    renderStatusBadge(slip.status)
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {slip.items?.length || 0} item(s)
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                <div className="flex items-center gap-2">
                                                    {editingSlipId === slip.id ? (
                                                        <>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleSaveStatus(slip.id);
                                                                }}
                                                                className="text-green-600 hover:text-green-900"
                                                            >
                                                                Save
                                                            </button>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleCancelEdit();
                                                                }}
                                                                className="text-gray-600 hover:text-gray-900"
                                                            >
                                                                Cancel
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleEditStatus(slip);
                                                                }}
                                                                className="text-orange-600 hover:text-orange-900"
                                                            >
                                                                Edit
                                                            </button>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    navigate(`/packing-slips/${slip.id}`);
                                                                }}
                                                                className="text-blue-600 hover:text-blue-900"
                                                            >
                                                                View
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Pagination */}
                    {!loading && packingSlips.length > 0 && (
                        <div className="flex justify-between items-center mt-6">
                            <div className="text-sm text-gray-700">
                                Showing <span className="font-medium">{pagination.total > 0 ? (pagination.currentPage - 1) * 15 + 1 : 0}</span> to <span className="font-medium">{Math.min(pagination.currentPage * 15, pagination.total)}</span> of <span className="font-medium">{pagination.total}</span> results
                            </div>
                            <div className="flex space-x-2">
                                <button
                                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                                    disabled={pagination.currentPage <= 1}
                                    className={`px-3 py-1 rounded-md ${
                                        pagination.currentPage <= 1
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                                    }`}
                                >
                                    Previous
                                </button>
                                <button
                                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                                    disabled={pagination.currentPage >= pagination.totalPages}
                                    className={`px-3 py-1 rounded-md ${
                                        pagination.currentPage >= pagination.totalPages
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                                    }`}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PackingSlipsList;
